import { Module } from "vuex";
import { MessageStatusEnum, Notifications } from "@/types";
import {
  getAllMessageByCurrentUser,
  getMessageByRessourceUid,
  updateMessageStatus,
} from "@/store/services/communication/notification";
import { replyMessageService, transferMessage } from "@/store/services/communication/messaging";

import store from '@/store'
import { SELECTED_USER_NETWORK } from "@/utils";

function senderResourceUid(notification: any): string {
  return (
    notification?.senderUid?.resourceUid ||
    notification?.senderUid ||
    notification?.senderuid?.resourceUid ||
    notification?.senderuid ||
    ""
  );
}

function metadataValue(notification: any, key: string): any {
  const metadata = Array.isArray(notification?.metadata) ? notification.metadata : [];
  return metadata.find((meta: any) => (meta?.key || "").toLowerCase() === key.toLowerCase())?.value;
}

function normalizeMaybeNullString(v: any): string | null {
  if (v === undefined || v === null) return null;
  const s = `${v}`.trim();
  if (!s) return null;
  const lower = s.toLowerCase();
  if (lower === "null" || lower === "undefined") return null;
  return s;
}

function extractExplicitMsgRoot(notification: any): string | null {
  const fromMeta = normalizeMaybeNullString(metadataValue(notification, "MSG_ROOT"));
  if (fromMeta) return fromMeta;
  const p = notification?.parameters || {};
  return normalizeMaybeNullString(
    p.MSG_ROOT ?? p.msg_root ?? p.Msg_Root ?? p.msgRoot ?? p.MSGROOT
  );
}

function notificationUid(notification: any): string {
  return (
    notification?.resourceUid ||
    notification?.resourceuid ||
    ""
  );
}

function notificationThreadKey(notification: any): string {
  // Thread key strategy:
  // - explicit MSG_ROOT when available (reply/transfer chain)
  // - otherwise fallback to own UID (root/standalone message)
  return extractExplicitMsgRoot(notification) || notificationUid(notification);
}

function communicationDateMs(notification: any): number {
  const d = notification?.communicationDate;
  if (!d) return 0;
  const t = new Date(d).getTime();
  return Number.isNaN(t) ? 0 : t;
}
const notificationModule: Module<Notifications, any> = {
  namespaced: true,
  state: {
    notifications: [],
    countNotifications: 0,
    msgUID: "",
    msgStatusTabs: ["ALL", "READ", "NEW"],
    msgSelectedStatusTab: "ALL"
  },
  mutations: {

    setSelectedStatusTab(state, payload) {
      state.msgSelectedStatusTab = payload
    },


    setNewMessage(state, payload) {
      state.notifications.unshift(payload);

      // Count only unread messages (exclude READ and CLOSED)
      state.countNotifications = state.notifications.filter(
        (el: any) =>
          el.status?.resourceUid !== MessageStatusEnum.READ &&
          el.status?.resourceUid !== MessageStatusEnum.CLOSED
      ).length;
    },
    /**
     * This mutation makes it possible to modify the list of module messages.
     * Updates the list of notifications by filtering messages with a "CLOSED" status and counting the number of unread messages.
     * @param {PanelNotification} state - The current state of the module.
     * @param {Message[]} payload - The new list of messages.
     * @return {void} The list of messages after the update.
     */
    setData(state, payload) {
      // Keep all messages, including CLOSED ones.
      state.notifications = payload || [];

      // Count only unread messages (exclude READ and CLOSED)
      state.countNotifications = state.notifications?.filter(
        (el: any) =>
          el.status?.resourceUid !== MessageStatusEnum.READ &&
          el.status?.resourceUid !== MessageStatusEnum.CLOSED
      ).length;
    },

    /**
     * This mutation allows to change the status of a message from "unread" to "read".
     * @param {PanelNotification} state - The current state of the module.
     * @param {number} payload - The index of the message to modify.
     * @return {empty}
     **/

    updateStateOfMessage(state, payload: { index: number; messageEvent: any; ActionType: string }) {
      const currentStatus = state.notifications[payload.index].status?.resourceUid;
      const isUnread =
        currentStatus !== MessageStatusEnum.READ &&
        currentStatus !== MessageStatusEnum.CLOSED;
      if (isUnread) {
        state.countNotifications--;
      }
      if (payload.ActionType === "Delete") {
        state.notifications.splice(payload.index, 1)
      }
    },

    /**
     * Delete message from message list.
     * @param {Notifications} state - The current state of the module.
     * @param {number} payload - The index of the message to be deleted.
     * @return {empty}
     **/

    deleteAnMessage(state, payload: { index: number; messageEvent: any }) {
      /**
       * Decrease notification counter if notification deleted
       * is unread.
       */
      const currentStatus = state.notifications[payload.index].status?.resourceUid;
      const isUnread =
        currentStatus !== MessageStatusEnum.READ &&
        currentStatus !== MessageStatusEnum.CLOSED;
      if (isUnread) {
        state.countNotifications--;
      }

      /**
       * Delete the notification from the index given in the notice board.
       */
      state.notifications = state.notifications.filter(
        (el, index) => index != payload.index
      );
    },

    setMsgUID(state, payload) {
      state.msgUID = payload;
    },

  },
  actions: {
    /**
     * This action retrieves notification messages and stores them in the module state.
     * @return {Promise<PanelNotification>}
     */
    async fetchData({ commit }, payload) {
      let _notifications: any = null;
      if (payload === null) { 
        try {
          const selectedUserNetworkJson=localStorage.getItem(SELECTED_USER_NETWORK)
          if(selectedUserNetworkJson ) {
            _notifications = await getAllMessageByCurrentUser();
            _notifications = _notifications.filter((notification: any) => {
              const entityIdRaw = metadataValue(notification, "Entity_Id");
              const entityId = typeof entityIdRaw === "string" ? entityIdRaw.trim() : entityIdRaw;
              if (entityId === selectedUserNetworkJson) return true;

              // Keep messages even when Entity_Id is absent (backend can return null for valid messages).
              const hasNoEntityId = entityId === null || entityId === undefined || entityId === "";
              if (hasNoEntityId) return true;
              return senderResourceUid(notification) === store.state.authModule?.user?.userId;
            });
                      /**
           *  sorted list of notifs by date Time
           */
          const sortedNotifications = _notifications.sort(
            (a: any, b: any) =>
              new Date(b.communicationDate).getTime() -
              new Date(a.communicationDate).getTime());
          /**
           *  trim attributes of notif.metadata
           *  */
          _notifications = sortedNotifications.map((notification: any) => {
            return {
              ...notification, metadata: [...(notification.metadata || [])].map((obj: any) => {
                obj.value = obj.value.trimEnd();
                return obj;
              })
            };
          });
          }
          commit("setData", _notifications);
        } catch (error) {
          console.error(error);
        }
      } else {
        const eventUid = payload?.ressourceUid || payload?.resourceUid;
        if (!eventUid) return;
        _notifications = await getMessageByRessourceUid(eventUid);
        const normalizedNotification = Array.isArray(_notifications) ? _notifications[0] : _notifications;
        if (!normalizedNotification) return;
        const senderUid = senderResourceUid(normalizedNotification);
        if (!senderUid || store.state.authModule?.user.userId != senderUid) {
          commit("setNewMessage", normalizedNotification);
        }
      }

    },


    updateSelectedStatusTab({ commit }, payload: any) {
      commit("setSelectedStatusTab", payload)
    },



    /**
     * This action changes the status of a message from "unread" to "read".
     * @param {number} payload - The index of the message to modify.
     * @return {empty}
     */
    updateState({ dispatch }, payload: any) {
      if(payload?.ActionType === "Delete"){
        store.dispatch("showConfirmationDeleteDialog", {
          message: `Are you sure you want to delete this notification?`,
          deleteAction: 'Notification',
          payload: payload,
          confirmation: true
        })
      }else{
        dispatch("updateStateOfMessage",payload);
      }
    },
     
    updateStateOfMessage({ commit }, payload: any) {
      const { index, notification, ActionType } = payload;
      commit("updateStateOfMessage", { index, ActionType })
      updateMessageStatus(notification, ActionType)
    },

    /**
     * Sets the unique identifier of the message to be replied to in the notification module state.
     *
     * @async
     * @function setMsgUID
     * @param {string} payload - The unique identifier of the message to be replied to.
     */
    setMsgUID({ commit }, payload) {
      commit("setMsgUID", payload);
    },
    /**
     * Sends a reply message to a system event using the replyMessageService.
     * @function messageReply
     * @param {Message} payload - The message to be sent as a reply.
     * @throws {Error} An error object containing details about the failed API call.
     */
    async messageReply(_ctx, payload) {
      try {
        await replyMessageService(payload);
      } catch (error) {
        console.error(error);
      }
    },
    /**
     * transfer selected message from notifications layout
     * @param commit
     * @param payload
     */
    async transferMessage(_ctx, payload) {
      try {
        const res = await new Promise((resolve, reject) => {
          transferMessage(payload)
            .then(resolve)
            .catch(reject);
        });
        return res;
      } catch (error) {
        console.error(error);
        throw error;
      }
    }


  },
  getters: {
    /**
     * Returns the list of notifications.
     * @param {PanelNotification} state - The current state of the module.
     * @return {Message[]} The list of notifications.
     */
    getData(state) {
      return state.notifications;
    },

    /**
     * Returns notifications grouped by MSG_ROOT (Messenger style).
     * - If MSG_ROOT is null/"null", the message is its own root (thread key = resourceUid)
     * - Replies/transfers share the same thread key (MSG_ROOT = root resourceUid)
     */
    getThreads(state) {
      const groups: Record<string, any[]> = {};
      for (const n of (state.notifications || []) as any[]) {
        const key = notificationThreadKey(n);
        if (!key) continue;
        if (!groups[key]) groups[key] = [];
        groups[key].push(n);
      }

      const threads = Object.keys(groups).map((key) => {
        const messages = groups[key].slice().sort((a, b) => communicationDateMs(a) - communicationDateMs(b));
        const root = messages.find((m) => notificationUid(m) === key) || messages[0];
        const last = messages[messages.length - 1];
        const unreadCount = messages.filter((m: any) =>
          m?.status?.resourceUid !== MessageStatusEnum.READ &&
          m?.status?.resourceUid !== MessageStatusEnum.CLOSED
        ).length;
        return {
          key,
          root,
          lastMessage: last,
          messages,
          replyCount: Math.max(0, messages.length - 1),
          unreadCount,
          lastDateMs: communicationDateMs(last),
        };
      });

      // Messenger-like: most recent thread first
      return threads.sort((a: any, b: any) => b.lastDateMs - a.lastDateMs);
    },

    /**
     * Convenience getter: returns only root notifications, enriched with reply count.
     * Useful for "thread list" UIs while keeping original getData intact.
     */
    getThreadedData(_state, getters: any) {
      const threads = getters?.getThreads || [];
      return threads.map((t: any) => ({
        ...t.root,
        _threadKey: t.key,
        _replyCount: t.replyCount,
        _unreadInThread: t.unreadCount,
        _lastMessageDate: t.lastMessage?.communicationDate || t.root?.communicationDate,
      }));
    },
    /**
     * Returns the number of unread notifications.
     * @param {PanelNotification} state - The current state of the module.
     * @return {number} The number of unread notifications.
     */
    getDataSize(state) {
      return state.countNotifications;
    },


    /**
     * Retrieves the unique identifier of the message to be replied to from the notification module state.
     * @returns {string} The unique identifier of the message to be replied to.
     */
    getMsgUID(state) {
      return state.msgUID;
    },
  },
};
export default notificationModule;
